'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { FileUpload } from '@/components/ui/file-upload'
import { AppProvider, useApp, Inquiry, Quote } from '@/lib/store'
import { 
  Wind, 
  Search, 
  FileText, 
  Clock, 
  Phone, 
  Mail, 
  MapPin,
  ChevronRight,
  ChevronLeft,
  User,
  LogIn,
  CheckCircle2,
  Building2,
  Award
} from 'lucide-react'

function HomeContent() {
  const { inquiries, quotes, isLoggedIn, currentUser, addQuote, procurementMethods, biddingRounds, refreshData } = useApp()
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null)
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [quoteForm, setQuoteForm] = useState({
    companyName: '',
    contactPerson: '',
    contactPhone: '',
    quoteAmount: '',
    attachment: ''
  })
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '安维士风电科技有限公司',
    phone: '400-888-9999',
    email: 'inquiry@anweisi.com',
    address: '北京市海淀区xxx大厦'
  })

  // 获取公司信息
  useEffect(() => {
    fetch('/api/company')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          setCompanyInfo(data.data)
        }
      })
      .catch(err => console.error('获取公司信息失败:', err))
  }, [])

  // 获取询价单的有效截止时间
  const getEffectiveDeadline = (inquiry: Inquiry): string => {
    const method = procurementMethods.find(m => m.methodCode === inquiry.procurementMethodCode)
    if (method?.allowMultiRound) {
      // 多轮报价：取业务状态为"生效"的轮次的报价截止时间
      const effectiveRound = biddingRounds.find(r => r.inquiryNo === inquiry.inquiryNo && r.businessStatus === '生效')
      if (effectiveRound?.quoteDeadline) {
        return effectiveRound.quoteDeadline
      }
      // 如果没有生效轮次，回退到使用询价单的截止时间
      return inquiry.quoteDeadline || ''
    }
    // 单轮报价：直接取询价单的报价截止时间
    return inquiry.quoteDeadline || ''
  }

  // 只显示已发布且报价截止时间未过的询价单
  const publishedInquiries = inquiries.filter(i => {
    if (i.publishStatus !== '已发布') return false
    const deadline = getEffectiveDeadline(i)
    if (!deadline) return false
    const deadlineDate = new Date(deadline.replace(' ', 'T'))
    return deadlineDate >= new Date()
  }).sort((a, b) => {
    const dateA = new Date(a.publishTime || a.createdAt)
    const dateB = new Date(b.publishTime || b.createdAt)
    return dateB.getTime() - dateA.getTime()
  })

  const filteredInquiries = publishedInquiries.filter(i =>
    i.materialName.includes(searchTerm) ||
    i.specification.includes(searchTerm) ||
    i.inquiryNo.includes(searchTerm)
  )

  // 分页计算
  const totalPages = Math.ceil(filteredInquiries.length / pageSize)
  const paginatedInquiries = filteredInquiries.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  // 搜索时重置到第一页
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const handleStartQuote = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry)
    if (!isLoggedIn) {
      window.location.href = '/login'
    } else {
      // 检查当前用户是否已对该询价单报过价
      const method = procurementMethods.find(m => m.methodCode === inquiry.procurementMethodCode)
      const isMultiRound = method?.allowMultiRound
      if (isMultiRound) {
        const activeRound = biddingRounds.find(r => r.inquiryNo === inquiry.inquiryNo && r.biddingStatus === '竞价中' && r.businessStatus === '生效')
        const existingQuote = activeRound
          ? quotes.find(q => q.inquiryNo === inquiry.inquiryNo && q.userId === currentUser?.id && q.biddingRound === activeRound.roundNumber)
          : null
        if (existingQuote) {
          alert(`本轮您已经报过价，不可重复报价。已报价的报价单号：${existingQuote.quoteNo}`)
          return
        }
        if (!activeRound) {
          alert('当前无进行中的竞价轮次，暂时无法报价')
          return
        }
      } else {
        const existingQuote = quotes.find(q => q.inquiryNo === inquiry.inquiryNo && q.userId === currentUser?.id)
        if (existingQuote) {
          alert(`您已经报过价，不可重复报价。已报价的报价单号：${existingQuote.quoteNo}`)
          return
        }
      }
      setShowQuoteModal(true)
    }
  }

  const handleSubmitQuote = () => {
    // 检查用户是否完善了公司信息
    if (!currentUser?.companyFullName || !currentUser?.companyContactPerson || !currentUser?.companyContactPhone) {
      alert('请先在个人信息页面完善公司信息后再报价')
      return
    }
    setShowQuoteModal(false)
    setShowConfirmModal(true)
  }

  const handleConfirmQuote = async () => {
    if (selectedInquiry && currentUser) {
      console.log('开始提交报价，用户信息:', {
        companyFullName: currentUser.companyFullName,
        companyContactPerson: currentUser.companyContactPerson,
        companyContactPhone: currentUser.companyContactPhone,
        userId: currentUser.id
      })
      // 获取当前竞价轮次（多轮报价时）
      const method = procurementMethods.find(m => m.methodCode === selectedInquiry.procurementMethodCode)
      const activeRound = method?.allowMultiRound
        ? biddingRounds.find(r => r.inquiryNo === selectedInquiry.inquiryNo && r.biddingStatus === '竞价中' && r.businessStatus === '生效')
        : null

      const success = await addQuote({
        companyName: currentUser.companyFullName || '',
        contactPerson: currentUser.companyContactPerson || '',
        contactPhone: currentUser.companyContactPhone || '',
        quoteAmount: quoteForm.quoteAmount ? parseFloat(quoteForm.quoteAmount) : undefined,
        attachment: quoteForm.attachment,
        inquiryNo: selectedInquiry.inquiryNo,
        userId: currentUser.id,
        status: '已报价',
        biddingRound: activeRound ? activeRound.roundNumber : 0
      })
      setShowConfirmModal(false)
      if (success) {
        setSubmitSuccess(true)
        setQuoteForm({ companyName: '', contactPerson: '', contactPhone: '', quoteAmount: '', attachment: '' })
        setTimeout(() => setSubmitSuccess(false), 3000)
      } else {
        alert('报价提交失败，请稍后重试')
      }
    }
  }

  // 获取已中选的记录
  const selectedRecords = quotes
    .filter(q => q.isSelected === '中选')
    .map(q => {
      const inquiry = inquiries.find(i => i.inquiryNo === q.inquiryNo)
      return {
        ...q,
        materialName: inquiry?.materialName || '-',
        selectedTime: q.selectedTime || '-'
      }
    })
    .sort((a, b) => {
      const timeA = new Date(a.selectedTime || 0).getTime()
      const timeB = new Date(b.selectedTime || 0).getTime()
      return timeB - timeA
    })

  return (
    <div className="min-h-screen bg-background">
      {/* 导航栏 */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 wind-gradient rounded-lg flex items-center justify-center">
              <Wind className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <span className="font-bold text-lg">安维士</span>
              <span className="text-muted-foreground text-sm ml-2">风电询价平台</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#inquiries" className="text-sm text-muted-foreground hover:text-foreground transition-colors">询价大厅</a>
            <a href="#about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">关于我们</a>
            <a href="#contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">联系方式</a>
          </div>
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <span className="text-sm text-muted-foreground hidden sm:inline">
                  欢迎，{currentUser?.username}
                </span>
                <Link href="/admin">
                  <Button variant="outline" size="sm">
                    <User className="w-4 h-4 mr-2" />
                    管理后台
                  </Button>
                </Link>
              </>
            ) : (
              <Link href="/login">
                <Button variant="wind" size="sm">
                  <LogIn className="w-4 h-4 mr-2" />
                  登录/注册
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* 中选公示 - 顶部展示 */}
      <section id="about" className="pt-20 pb-6 relative overflow-hidden">
        {/* 背景图片 */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/images/wind-energy-hero.png)' }}
        />
        {/* 背景遮罩 */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/85 via-background/75 to-background/90" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-4">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Award className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-bold">中选公示</h2>
            </div>
            <p className="text-muted-foreground text-xs">最新中选结果公示</p>
          </div>
          
          {selectedRecords.length === 0 ? (
            <Card className="max-w-3xl mx-auto">
              <CardContent className="text-center py-8">
                <Award className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-sm">暂无中选公示信息</p>
              </CardContent>
            </Card>
          ) : (
            <div className="max-w-6xl mx-auto overflow-hidden">
              <div className="relative h-[220px] overflow-hidden">
                <div className="animate-scroll-up space-y-2">
                  {/* 复制两份数据实现无缝滚动 */}
                  {[...selectedRecords, ...selectedRecords].map((record, index) => (
                    <Card key={`${record.id}-${index}`} className="hover:border-primary/30 transition-all">
                      <CardContent className="px-4 sm:px-6 py-3">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                            <Badge variant="success" className="shrink-0">中选</Badge>
                            <span className="text-muted-foreground flex items-center gap-1">
                              <Building2 className="w-3.5 h-3.5 shrink-0" />
                              <span>{record.companyName}</span>
                            </span>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-4 flex-wrap flex-1">
                            <span className="font-medium">{record.materialName}</span>
                            <span className="font-mono text-sm text-muted-foreground">{record.inquiryNo}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-muted-foreground text-xs">
                              {record.selectedTime}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 询价大厅 */}
      <section id="inquiries" className="pt-6 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">询价大厅</h2>
            <p className="text-muted-foreground">查看最新发布的询价信息，立即参与报价</p>
          </div>

          {/* 搜索栏 */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="搜索物料名称、规格型号或询价单号..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12"
              />
            </div>
          </div>

          {/* 询价列表 */}
          <div className="space-y-4">
            {filteredInquiries.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">暂无符合条件的询价信息</p>
                </CardContent>
              </Card>
            ) : (
              paginatedInquiries.map((inquiry) => (
                <Card key={inquiry.id} className="hover:border-primary/30 transition-all">
                  <CardContent className="px-6 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 flex-1 min-w-0">
                        <h3 className="font-semibold truncate min-w-0">{inquiry.materialName}</h3>
                        <div className="flex items-center gap-3 shrink-0 text-sm">
                          <span className="flex items-center gap-1 text-destructive whitespace-nowrap">
                            <Clock className="w-3.5 h-3.5" />
                            截止：{getEffectiveDeadline(inquiry)}
                          </span>
                          <span className="items-center gap-1 text-muted-foreground whitespace-nowrap hidden md:flex">
                            <Clock className="w-3.5 h-3.5" />
                            发布：{inquiry.publishTime}
                          </span>
                        </div>
                      </div>
                      {(() => {
                        const deadline = getEffectiveDeadline(inquiry)
                        if (!deadline) return null
                        return new Date() <= new Date(deadline.replace(' ', 'T')) ? (
                          <Button variant="wind" size="sm" className="shrink-0" onClick={() => handleStartQuote(inquiry)}>
                            开始报价
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        ) : null
                      })()}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* 分页控件 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                上一页
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <Button
                    key={page}
                    variant={currentPage === page ? "wind" : "outline"}
                    size="sm"
                    className="w-9 h-9 p-0"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                下一页
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* 显示总数信息 */}
          {filteredInquiries.length > 0 && (
            <div className="text-center text-sm text-muted-foreground mt-4">
              共 {filteredInquiries.length} 条询价信息，当前第 {currentPage} 页 / 共 {totalPages} 页
            </div>
          )}
        </div>
      </section>

      {/* 联系方式 */}
      <section id="contact" className="py-6 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <h2 className="text-lg font-bold mb-1">联系我们</h2>
            <p className="text-muted-foreground text-xs">如有任何问题，欢迎随时联系</p>
          </div>
          <div className="grid md:grid-cols-3 gap-3 max-w-3xl mx-auto">
            <Card className="text-center group hover:-translate-y-1 transition-all">
              <CardContent className="pt-4 pb-3">
                <div className="w-8 h-8 wind-gradient rounded-full flex items-center justify-center mx-auto mb-2">
                  <Phone className="w-4 h-4 text-primary-foreground" />
                </div>
                <h3 className="font-semibold text-sm mb-1">电话咨询</h3>
                <p className="text-muted-foreground text-xs">{companyInfo.phone}</p>
              </CardContent>
            </Card>
            <Card className="text-center group hover:-translate-y-1 transition-all">
              <CardContent className="pt-4 pb-3">
                <div className="w-8 h-8 wind-gradient rounded-full flex items-center justify-center mx-auto mb-2">
                  <Mail className="w-4 h-4 text-primary-foreground" />
                </div>
                <h3 className="font-semibold text-sm mb-1">邮件联系</h3>
                <p className="text-muted-foreground text-xs">{companyInfo.email}</p>
              </CardContent>
            </Card>
            <Card className="text-center group hover:-translate-y-1 transition-all">
              <CardContent className="pt-4 pb-3">
                <div className="w-8 h-8 wind-gradient rounded-full flex items-center justify-center mx-auto mb-2">
                  <MapPin className="w-4 h-4 text-primary-foreground" />
                </div>
                <h3 className="font-semibold text-sm mb-1">公司地址</h3>
                <p className="text-muted-foreground text-xs">{companyInfo.address}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 页脚 */}
      <footer className="bg-foreground text-background py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-background rounded-lg flex items-center justify-center">
                  <Wind className="w-5 h-5 text-foreground" />
                </div>
                <span className="font-bold text-lg">安维士</span>
              </div>
              <p className="text-background/70 text-sm">
                专业的风电新能源设备在线询价平台
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">快速链接</h4>
              <ul className="space-y-2 text-sm text-background/70">
                <li><a href="#inquiries" className="hover:text-background transition-colors">询价大厅</a></li>
                <li><a href="#about" className="hover:text-background transition-colors">关于我们</a></li>
                <li><a href="#contact" className="hover:text-background transition-colors">联系方式</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">服务支持</h4>
              <ul className="space-y-2 text-sm text-background/70">
                <li><a href="#" className="hover:text-background transition-colors">帮助中心</a></li>
                <li><a href="#" className="hover:text-background transition-colors">常见问题</a></li>
                <li><Link href="/login" className="hover:text-background transition-colors">登录/注册</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">联系方式</h4>
              <ul className="space-y-2 text-sm text-background/70">
                <li>电话：{companyInfo.phone}</li>
                <li>邮箱：{companyInfo.email}</li>
                <li>地址：{companyInfo.address}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-background/20 mt-8 pt-8 text-center text-sm text-background/50">
            <p>苏ICP16053427号</p>
            <p className="mt-1">Copyright &copy; 2017 南京安维士传动技术股份有限公司. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* 报价弹窗 */}
      <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
        <DialogContent onClose={() => setShowQuoteModal(false)} className="max-w-md">
          <DialogHeader>
            <DialogTitle>提交报价</DialogTitle>
            <DialogDescription>
              询价单号：{selectedInquiry?.inquiryNo}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-lg text-sm">
              <p className="font-medium">{selectedInquiry?.materialName}</p>
              <p className="text-muted-foreground">{selectedInquiry?.specification}</p>
              {selectedInquiry?.limitPrice && (
                <p className="text-primary font-medium mt-2">限价：¥{selectedInquiry?.limitPrice}</p>
              )}
            </div>
            <div className="p-3 bg-primary/5 rounded-lg text-sm space-y-1">
              <p className="font-medium text-primary mb-1">报价单位信息（来自个人信息）</p>
              <p><span className="text-muted-foreground">公司名称：</span>{currentUser?.companyFullName || '未填写'}</p>
              <p><span className="text-muted-foreground">联系人：</span>{currentUser?.companyContactPerson || '未填写'}</p>
              <p><span className="text-muted-foreground">联系电话：</span>{currentUser?.companyContactPhone || '未填写'}</p>
              {(!currentUser?.companyFullName || !currentUser?.companyContactPerson || !currentUser?.companyContactPhone) && (
                <p className="text-destructive text-xs mt-2">* 请先在个人信息页面完善公司信息</p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">报价金额（元）*</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">¥</span>
                <Input
                  type="number"
                  placeholder="请输入报价金额"
                  value={quoteForm.quoteAmount}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, quoteAmount: e.target.value }))}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">上传附件</label>
              <FileUpload
                value={quoteForm.attachment}
                onUpload={(fileUrl) => setQuoteForm(prev => ({ ...prev, attachment: fileUrl }))}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
                maxSize={10}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuoteModal(false)}>取消</Button>
            <Button 
              variant="wind" 
              onClick={handleSubmitQuote}
              disabled={!quoteForm.quoteAmount}
            >
              提交报价
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 确认弹窗 */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent onClose={() => setShowConfirmModal(false)} className="max-w-sm">
          <DialogHeader>
            <DialogTitle>确认报价</DialogTitle>
            <DialogDescription>
              请确认您的报价信息无误
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2 text-sm">
            <p><span className="text-muted-foreground">报价单位：</span>{currentUser?.companyFullName}</p>
            <p><span className="text-muted-foreground">联系人：</span>{currentUser?.companyContactPerson}</p>
            <p><span className="text-muted-foreground">联系方式：</span>{currentUser?.companyContactPhone}</p>
            <p><span className="text-muted-foreground">报价金额：</span>¥{quoteForm.quoteAmount}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>返回修改</Button>
            <Button variant="wind" onClick={handleConfirmQuote}>确认提交</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 成功提示 */}
      {submitSuccess && (
        <div className="fixed bottom-8 right-8 z-50 animate-fade-up">
          <Card className="bg-primary text-primary-foreground">
            <CardContent className="flex items-center gap-3 py-4">
              <CheckCircle2 className="w-5 h-5" />
              <span>报价提交成功！</span>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export default function Home() {
  return (
    <AppProvider>
      <HomeContent />
    </AppProvider>
  )
}
